import React, {forwardRef, useEffect, useRef, useState} from "react";
import {isEmail} from "validator";
import {alpha, styled, useTheme} from "@mui/material/styles";
import {unstable_useTreeItem2 as useTreeItem} from "@mui/x-tree-view/useTreeItem2";
import {TreeItem2Provider} from "@mui/x-tree-view/TreeItem2Provider";
import clsx from "clsx";
import {TreeItem2Icon} from "@mui/x-tree-view/TreeItem2Icon";
import {Box, Typography} from "@mui/material";
import {
    TreeItem2Content,
    TreeItem2GroupTransition,
    TreeItem2IconContainer,
    TreeItem2Root
} from "@mui/x-tree-view/TreeItem2";
import {
    GiBigWave,
    GiBlockHouse,
    GiCastle,
    GiCaveEntrance, GiColiseum,
    GiCryptEntrance,
    GiDesert,
    GiDungeonGate,
    GiForest,
    GiGalaxy,
    GiHighGrass,
    GiHutsVillage,
    GiIsland,
    GiJungle,
    GiMedievalGate,
    GiPeaks, GiShintoShrine,
    GiShoonerSailboat,
    GiSnowing,
    GiSwamp,
    GiTavernSign
} from "../icons/startupGameIcons";
import {MdOutlineWater, MdPhoto} from "react-icons/md";
import {RiFolderImageFill} from "react-icons/ri";
import {FaCloudSun} from "react-icons/fa";
import Swal from "sweetalert2";

export const required = value => {
    if (!value) {
        return (
            <div className="alert alert-danger" role="alert">
                This field is required!
            </div>
        );
    }
};

export const vEmail = value => {
    if (!isEmail(value)) {
        return (
            <div className="alert alert-danger" role="alert">
                This is not a valid email.
            </div>
        );
    }
};

export const vUsername = value => {
    if (value.length < 3 || value.length > 20) {
        return (
            <div className="alert alert-danger" role="alert">
                The username must be between 3 and 20 characters.
            </div>
        );
    }
};

export const vPassword = value => {
    if (value.length < 6 || value.length > 40) {
        return (
            <div className="alert alert-danger" role="alert">
                The password must be between 6 and 40 characters.
            </div>
        );
    }
};

export const vConfirmPassword = (value, currentPassword) => {
    if (value !== currentPassword) {
        return (
            <div className="alert alert-danger" role="alert">
                The passwords do not match.
            </div>
        );
    }
};

// Validation logic for email format
export const isEmailValid = (email) => {
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailPattern.test(email);
};

export function byteConverter( bytes, decimals, only) {
    const K_UNIT = 1024;
    const SIZES = ["Bytes", "KB", "MB", "GB", "TB", "PB"];

    if(bytes === 0) return "0 Byte";
    if(only === "MB") return (bytes / (K_UNIT*K_UNIT)).toFixed(decimals) + " MB" ;

    let i = Math.floor(Math.log(bytes) / Math.log(K_UNIT));
    return parseFloat((bytes / Math.pow(K_UNIT, i)).toFixed(decimals)) + " " + SIZES[i];
}

export function timeout(delay) {
    return new Promise( res => setTimeout(res, delay) );
}

// Backend error responses (GlobalExceptionHandler) come back as {errors: ["msg", ...]}.
// Upload endpoints in particular can now reject with a specific reason (bad file type,
// failed moderation) that's worth surfacing instead of a generic failure message.
export async function getErrorMessage(response, fallback = 'Something went wrong. Please try again.') {
    try {
        const data = await response.json();
        if (Array.isArray(data?.errors) && data.errors.length > 0) {
            return data.errors.join(' ');
        }
    } catch (_) {}
    return fallback;
}

// The cached 'user' object in sessionStorage is written once at login and never
// refreshed automatically — self-service toggles (e.g. isCreator) that need the
// UI to reflect a change immediately must merge their result back in via this.
export function updateCachedUser(patch) {
    const userJSON = JSON.parse(sessionStorage.getItem('user') || '{}');
    const updated = {...userJSON, ...patch};
    sessionStorage.setItem('user', JSON.stringify(updated));
    return updated;
}

// Sage conversations are kept on the server (useSageTranscript); the tab holds a copy in
// sessionStorage (`sageChat_...`, `sageBestiaryChat_...`) for an instant paint. Logout only
// clears the 'user' key, so the copies are swept explicitly - on a shared computer they
// must not outlive the session that produced them.
export function clearSageHistory() {
    Object.keys(sessionStorage)
        .filter(key => key.startsWith('sageChat_') || key.startsWith('sageBestiaryChat_'))
        .forEach(key => sessionStorage.removeItem(key));
}

// Asks before logging out, so a stray click on a logout button doesn't end the session.
// Resolves true when the user confirms.
export function confirmLogout() {
    return Swal.fire({
        title: 'Log out?',
        text: 'You will need to sign in again to get back to your campaigns.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Log Out',
        confirmButtonColor: '#d33',
        cancelButtonText: 'Cancel',
        focusCancel: true,
        background: 'var(--th-1e1a2a)',
        color: '#F5F5F5',
    }).then(result => result.isConfirmed);
}

// A map's identity across the tree, the presigned-URL cache, the board-state caches and the
// MAP_UPDATE broadcast is its objectKey. Dropbox-backed maps have no S3 object, so their
// objectKey is persisted as an empty string - every such map would share that blank identity,
// collide as a duplicate tree itemId and resolve to whichever one happens to come first. Fall
// back to the row id for those so each map still has a unique, stable key.
export function mapKeyOf(map) {
    if (!map) return null;
    return map.objectKey ? map.objectKey : (map.id ? 'map-' + map.id : null);
}

// True only for something that can actually be used as an <img src>. Guards the endpoints
// that hand back a URL as text/plain, where an error body is otherwise indistinguishable
// from a result.
export function isHttpUrl(value) {
    const v = String(value || '').trim().toLowerCase();
    return v.startsWith('http://') || v.startsWith('https://');
}

// Resolves a key produced by mapKeyOf back to its map row.
export function findMapByKey(maps, key) {
    if (!Array.isArray(maps) || !key) return undefined;
    return maps.find(m => mapKeyOf(m) === key);
}

export const convertByteArrayToImage= (bytes, fileType) => {
    const byteCharacters = atob(bytes);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    let image = new Blob([byteArray], { type: fileType });
    return URL.createObjectURL(image);
}

const CustomTreeItemRoot = styled(TreeItem2Root)(({theme}) => ({
    color: theme.palette.text,
}));

const CustomTreeItemContent = styled(TreeItem2Content)(({theme}) => ({
    marginBottom: theme.spacing(0.3),
    color: theme.palette.text,
    borderRadius: theme.spacing(2),
    paddingRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    '&.expanded': {
        fontWeight: theme.typography.fontWeightRegular,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
    '&.focused, &.selected, &.selected.focused': {
        backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
        color: 'var(--tree-view-color)',
    },
}));

const CustomTreeItemIconContainer = styled(TreeItem2IconContainer)(({theme}) => ({
    marginRight: theme.spacing(1),
}));

const CustomTreeItemGroupTransition = styled(TreeItem2GroupTransition)(
    ({theme}) => ({
        marginLeft: 0,
        [`& .content`]: {
            paddingLeft: theme.spacing(2),
        },
    }),
);

export function EndIcon() {
    return <div style={{width: 24}}/>;
}

export const treeIcons = {
    CASTLE: GiCastle,
    CAVE: GiCaveEntrance,
    CITY: GiMedievalGate,
    DESERT: GiDesert,
    DUNGEON: GiDungeonGate,
    FOREST: GiForest,
    GRASSLAND: GiHighGrass,
    HOUSE: GiBlockHouse,
    INN: GiTavernSign,
    ISLAND: GiIsland,
    JUNGLE: GiJungle,
    MAP: MdPhoto,
    MOUNTAIN: GiPeaks,
    OTHER: RiFolderImageFill,
    RUIN: GiCryptEntrance,
    SCIFI: GiGalaxy,
    SHIP: GiShoonerSailboat,
    SKY: FaCloudSun,
    SNOW: GiSnowing,
    SWAMP: GiSwamp,
    TOWN: GiHutsVillage,
    WATER: GiBigWave,
    ARENA: GiColiseum,
    MONASTERY: GiShintoShrine
};

export const CustomTreeItem = forwardRef(function CustomTreeItem(props, ref) {
    const theme = useTheme();
    const {
        id,
        itemId,
        label,
        disabled,
        children,
        bgColor,
        color,
        labelIcon: LabelIcon,
        labelInfo,
        customIcon,
        colorForDarkMode,
        bgColorForDarkMode,
        ...other
    } = props;

    const {
        getRootProps,
        getContentProps,
        getIconContainerProps,
        getLabelProps,
        getGroupTransitionProps,
        status,
    } = useTreeItem({id, itemId, children, label, disabled, rootRef: ref});

    const style = {
        '--tree-view-color': theme.palette.mode !== 'dark' ? color : colorForDarkMode,
        '--tree-view-bg-color':
            theme.palette.mode !== 'dark' ? bgColor : bgColorForDarkMode,
    };

    return (
        <TreeItem2Provider itemId={itemId}>
            <CustomTreeItemRoot {...getRootProps({...other, style})}>
                <CustomTreeItemContent
                    {...getContentProps({
                        className: clsx('content', {
                            expanded: status.expanded,
                            selected: status.selected,
                            focused: status.focused,
                        }),
                    })}
                >
                    <CustomTreeItemIconContainer {...getIconContainerProps()}>
                        <TreeItem2Icon status={status}/>
                    </CustomTreeItemIconContainer>
                    <Box
                        sx={{
                            display: 'flex',
                            flexGrow: 1,
                            alignItems: 'center',
                            p: 0.5,
                            pr: 0,
                        }}
                    >
                        {(customIcon === "" || LabelIcon === treeIcons["MAP"]) &&
                            <Box component={LabelIcon} size={32} color="inherit" sx={{mr: 1}}/>
                        }
                        {customIcon !== "" &&
                            <Box color="inherit" size={24}>
                                <i className={customIcon} style={{marginRight: "8px"}}/>
                            </Box>
                        }
                        <Typography
                            {...getLabelProps({
                                variant: 'body2',
                                sx: {display: 'flex', fontWeight: 'inherit', flexGrow: 1},
                            })}
                        />
                        <Typography variant="caption" color="inherit">
                            {labelInfo}
                        </Typography>
                    </Box>
                    {/*{!itemId.includes("-root") &&*/}
                    {/*    <IconButton aria-label="editMap" color="secondary" size="small" onClick={handleShowEditMap}>*/}
                    {/*        <Edit fontSize="inherit"/>*/}
                    {/*    </IconButton>*/}
                    {/*}*/}
                    {/*{!itemId.includes("-root") &&*/}
                    {/*    <IconButton aria-label="deleteMap" color="secondary" size="small" onClick={handleShowEditMap}>*/}
                    {/*        <DeleteForever fontSize="inherit"/>*/}
                    {/*    </IconButton>*/}
                    {/*}*/}
                </CustomTreeItemContent>
                {children && (
                    <CustomTreeItemGroupTransition {...getGroupTransitionProps()} />
                )}
            </CustomTreeItemRoot>
        </TreeItem2Provider>
    );
});

export function getRandomInt(max) {
    return Math.floor(Math.random() * max);
}

// D&D 5e space rule: a Medium creature occupies one grid square; other sizes
// scale relative to that square (Small shares Medium's square; Large/Huge/
// Gargantuan occupy 2x2/3x3/4x4 squares).
export const SIZE_CELL_MULTIPLIERS = {
    Tiny: 0.5,
    Small: 1,
    Medium: 1,
    Large: 2,
    Huge: 3,
    Gargantuan: 4,
};

export function tokenSize(monster, cellSize = 60) {
    const normalized = monster.size?.charAt(0).toUpperCase() + monster.size?.slice(1).toLowerCase();
    const multiplier = SIZE_CELL_MULTIPLIERS[normalized] ?? SIZE_CELL_MULTIPLIERS.Medium;
    const px = Math.round((cellSize || 60) * multiplier);
    monster.width = px + 'px';
    monster.height = px + 'px';
    monster.rawSize = px;
}

export function tokenColor(monster) {
    const randColor = Math.random().toString(16).substr(-6);  //generates a random color

    monster.color = '#' + randColor;
}

export const color = alpha('#FFFFFF', 1.0);
// Was alpha('#6C4F9B', 0.8); MUI's alpha() can't read a CSS variable, so the rgba is spelled out.
export const bgColor = 'rgba(var(--th-6c4f9b-rgb), 0.8)';

export const convertVhToPx = (vh) => {
    const oneVhInPx = window.innerHeight / 100;

    return oneVhInPx * vh;
};

export const convertVwToPx = (vw) => {
    const oneVwInPx = window.innerWidth / 100;

    return oneVwInPx * vw;
};

// Variable to store the current cursor position
export let cursorX = 0;
export let cursorY = 0;

export function useWindowSize() {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        function handleResize() {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });

            // Calculate the cursor's offset relative to the document or window
            const offsetX = cursorX / window.innerWidth * 100;  // Percentage of horizontal position
            const offsetY = cursorY / window.innerHeight * 100; // Percentage of vertical position


        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    return windowSize;
}

export async function getProfileForUser(userJSON) {
    //let avatarImage = null, avatarBorderColor= null, avatarFileType= null, avatarFile = null;

    const headers = {'Authorization': 'Bearer ' + userJSON.accessToken};
    fetch(`${process.env.REACT_APP_API_PROXY}/profile/getProfileForUser/${userJSON.id}`, {headers})
        .then((res) => {
            return res.json();
        })
        .then((data) => {
            // avatarImage = convertByteArrayToImage(data.avatarImage, data.fileType);
            // avatarBorderColor = data.avatarBorderColor;
            // avatarFileType = data.fileType;
            // avatarFile = data.avatarImage;
            return data;
        })
    // return {avatarImage, avatarBorderColor, avatarFileType, avatarFile};
}

export async function handleAddLogEntry(newEntry, entries, setEntries, userJSON, currentCampaignId) {
    return fetch(`${process.env.REACT_APP_API_PROXY}/logs/addEntry/${currentCampaignId}`, {
        method: 'POST',
        headers: {
            'Authorization': 'Bearer ' + userJSON.accessToken,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(newEntry)
    })
        .then(response => response.json())
        .then((data) => {
            const newEntries = [...entries, data];
            setEntries(newEntries);
        })
        .catch(err => {
            console.error('Request failed', err)
            // Swal.fire({
            //     timer: 2000,
            //     text: err,
            //     icon: 'error',
            //     position: "bottom-end"
            // });
        })
        .finally(() => {
            // Swal.fire({
            //     timer: 2000,
            //     text: 'Log Entry added successfully',
            //     icon: 'success',
            //     showConfirmButton: false,
            //     position: "bottom-end"
            // });
        })
}

export function getRandomIndex(array) {
    return Math.floor(Math.random() * array.length);
}

// Clamp an HP value to [0, maxHp] (maxHp null/undefined = no upper bound).
// Shared by the manual HP editor (TokenDetailsPanel) and the dice roller's
// apply-damage/heal action so both floor at 0 and cap at max the same way.
export function clampHp(hp, maxHp) {
    if (hp == null || isNaN(hp)) return null;
    let resolved = hp;
    if (maxHp != null && !isNaN(maxHp)) resolved = Math.min(resolved, maxHp);
    return Math.max(0, resolved);
}

// Apply a +/- HP delta to a token (heal: positive delta, damage: negative delta),
// auto-toggling 'dead' at 0 HP and clearing it if healed back above 0.
export function applyHpDelta(token, delta, updateTokenStats) {
    if (!updateTokenStats) return;
    const currentHp = token.hp != null ? token.hp : (token.maxHp ?? 0);
    const resolvedHp = clampHp(currentHp + delta, token.maxHp ?? null);
    const updates = {hp: resolvedHp};
    if (resolvedHp === 0 && token.status !== 'dead') updates.status = 'dead';
    else if (resolvedHp > 0 && token.status === 'dead') updates.status = '';
    updateTokenStats(token.key, updates);
}

// ── Minions ──────────────────────────────────────────────────────────────────
// A minion is a non-player token the GM has handed to a party character — a paladin's
// skeletons, a druid's summons. The assignment lives on the token itself as
// `minionOf: <characterId>`, so it rides along in the board-state blob and the
// TOKEN_STATS websocket sync without a schema of its own.
//
// Deliberately NOT the same thing as vision: minions are always visible to their
// controller (see visibleMonsterKeys in TableTop) but emit no line of sight and reveal
// no fog, so scouting with one can't uncover the map for the party. Vision stays keyed
// on myPlayerTokenKeys.
export function isMinionOf(token, characterIds) {
    return !!token?.minionOf && Array.isArray(characterIds) && characterIds.includes(token.minionOf);
}

// Single source of truth for "may this user drive this token". The board (move/resize/
// rotate) and the panels (HP/AC/conditions, stat block) both ask this, so they can never
// drift into disagreeing about who controls what. Deleting a minion is deliberately not
// covered here — that stays with the GM, who is the only one who can un-assign it.
export function canControlToken(token, {isOwner = false, myCharacterIds = []} = {}) {
    if (isOwner) return true;
    if (!token) return false;
    if (token.isPlayer && Array.isArray(myCharacterIds) && myCharacterIds.includes(token.characterId)) return true;
    return isMinionOf(token, myCharacterIds);
}

// ── Token image framing ─────────────────────────────────────────────────────
// A token's art always fills its frame (object-fit: cover); the owner picks which part
// shows via a focus point (imagePosX/Y, 0-100%) and a zoom factor. All three are relative,
// so they need no natural/container conversion and survive board resizes as-is.
export const TOKEN_IMAGE_ZOOM_MIN = 1;
export const TOKEN_IMAGE_ZOOM_MAX = 4;

export function getTokenImageFraming(token) {
    const clamp = (v, lo, hi, dflt) => (Number.isFinite(v) ? Math.max(lo, Math.min(hi, v)) : dflt);
    return {
        zoom: clamp(token?.imageZoom, TOKEN_IMAGE_ZOOM_MIN, TOKEN_IMAGE_ZOOM_MAX, 1),
        x:    clamp(token?.imagePosX, 0, 100, 50),
        y:    clamp(token?.imagePosY, 0, 100, 50),
    };
}

// A character's portrait framing expressed as board-token fields. Both use the same
// size-independent {zoom, x%, y%} model, so a token made from (or kept in step with) the
// portrait shows the same crop.
export function tokenFramingFromPortrait(character) {
    const f = getTokenImageFraming({
        imageZoom: character?.portraitZoom, imagePosX: character?.portraitPosX, imagePosY: character?.portraitPosY,
    });
    return {imageZoom: f.zoom, imagePosX: f.x, imagePosY: f.y};
}

// Style for an <img> inside an overflow:hidden box. Scaling about the focus point keeps
// that point fixed, so zooming in closes in on whatever the owner centred.
export function tokenImageFramingStyle({zoom, x, y}) {
    return {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: `${x}% ${y}%`,
        transform: zoom !== 1 ? `scale(${zoom})` : undefined,
        transformOrigin: `${x}% ${y}%`,
    };
}
